import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { Wheat, Globe, CheckCircle2, AlertTriangle, Circle, ShieldAlert } from "lucide-react";

export function BrandMark({ className = "", color = "#1F4D38" }) {
  return (
    <svg viewBox="0 0 40 40" className={className} role="img" aria-label="Żywiąca Polska" style={{ color }}>
      <path d="M4 20a16 16 0 0 0 32 0" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M9 23a11 11 0 0 0 22 0" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55" />
      <path d="M13 27a7 7 0 0 0 14 0" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.35" />
      <path d="M20 19V11" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M20 16c-4-1-6-4-5-7 3 0 6 2 5 7Z" fill="currentColor" />
      <path d="M20 16c4-1 6-4 5-7-3 0-6 2-5 7Z" fill="currentColor" />
      <circle cx="20" cy="8.5" r="3" fill="#D5A63A" />
    </svg>
  );
}

export function Logo({ light = false, className = "" }) {
  const { t } = useT();
  return (
    <Link to="/" className={`flex items-center gap-2.5 ${className}`} data-testid="brand-logo">
      <BrandMark className="h-9 w-9 shrink-0" color={light ? "#F5F1E7" : "#1F4D38"} />
      <span className={`font-heading text-lg font-extrabold leading-none tracking-tight ${light ? "text-white" : "text-forest"}`}>
        {t("brand")}
      </span>
    </Link>
  );
}

export function LanguageSwitcher({ light = false }) {
  const { lang, changeLang } = useT();
  return (
    <div className="flex items-center gap-1 rounded-md border border-white/30 p-0.5" role="group" aria-label="Language">
      {["pl", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => changeLang(l)}
          className={`rounded px-2 py-0.5 text-xs font-semibold uppercase transition-colors ${
            lang === l
              ? "bg-white text-forest"
              : light ? "text-white/80 hover:text-white" : "text-white/70 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function DemoBadge({ className = "" }) {
  const { t } = useT();
  return (
    <span data-testid="demo-badge" className={`inline-flex items-center gap-1 rounded border border-soil/30 bg-soil/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-soil ${className}`}>
      <Circle className="h-2 w-2 fill-soil" aria-hidden="true" /> {t("demo")}
    </span>
  );
}

const OK = ["Verified", "Fully matched", "Delivered", "Accepted", "Settled", "Resolved", "Closed", "Ready", "Approved"];
const WARN = ["At risk", "Partially matched", "Buyer matching", "Matching", "Verification pending", "Under review", "In transit", "Processing", "Open", "Requested", "Draft", "Forecast"];
const CRIT = ["Disputed", "Rejected or Unsafe", "Cancelled", "Withdrawn", "Exception", "Escalated"];

export function StatusBadge({ status }) {
  let cls = "bg-secondary text-secondary-foreground border-subtle";
  let Icon = Circle;
  if (OK.includes(status)) { cls = "bg-okbg text-oktext border-oktext/20"; Icon = CheckCircle2; }
  else if (CRIT.includes(status)) { cls = "bg-critbg text-crittext border-crittext/20"; Icon = ShieldAlert; }
  else if (WARN.includes(status)) { cls = "bg-warnbg text-warntext border-warntext/20"; Icon = AlertTriangle; }
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden="true" /> {status}
    </span>
  );
}

export function RiskBadge({ priority }) {
  const map = {
    high: { cls: "bg-critbg text-crittext border-crittext/20", label: "High" },
    medium: { cls: "bg-warnbg text-warntext border-warntext/20", label: "Medium" },
    low: { cls: "bg-secondary text-mutedtext border-subtle", label: "Low" },
  };
  const m = map[priority] || map.low;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-semibold uppercase ${m.cls}`}>
      <AlertTriangle className="h-3 w-3" aria-hidden="true" /> {m.label}
    </span>
  );
}

export function Metric({ label, value, sub }) {
  return (
    <div className="rounded-md border border-subtle bg-white p-4">
      <div className="text-2xl font-bold text-forest font-heading">{value}</div>
      <div className="mt-1 text-sm text-mutedtext">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-mutedtext/70">{sub}</div>}
    </div>
  );
}

export function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-3xl">
      {eyebrow && <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-soil">{eyebrow}</div>}
      <h2 className="font-heading text-2xl font-bold text-charcoal sm:text-3xl lg:text-4xl">{title}</h2>
      {children && <p className="mt-4 text-base leading-relaxed text-mutedtext">{children}</p>}
    </div>
  );
}
